// Firebase Notification System - Fixed
// This is a complete implementation using Firebase Cloud Messaging (FCM)

// 1. FIREBASE CONFIGURATION
// firebase-config.js
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { messaging } from "./FirebaseConfig";
import { getCurrentUser, getOrRefreshFirebaseToken } from "./getFirebaseToken";
import { getDocDataById } from "../models/DataFunctions";
import { Alert, Modal } from "antd";

const vapidKey =
  "BIa1I34J_xP4d8_2yqMXs7hMbuNJ7DRCCLH1Htdcfihr2dqfrp2LFHLMIJDww4d6G_BmEaB9cuSadTUtErnnwbk";

// 2. CLIENT-SIDE NOTIFICATION HANDLER
class NotificationManager {
  constructor() {
    this.token = null;
    this.isSupported = "serviceWorker" in navigator && "PushManager" in window;
    this.serviceWorkerRegistration = null;
  }

  async initialize(user_id, prefix, feedback = true) {
    if (!this.isSupported) {
      throw new Error("Push notifications are not supported");
    }

    // Register service worker and store registration
    this.serviceWorkerRegistration = await this.registerServiceWorker();
    console.log("Permission for notifications:", Notification.permission);
    if (Notification.permission !== "granted") {
      permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission denied");
      }
    }

    // Get FCM token
    this.token = await this.getFCMToken(user_id, prefix, feedback);

    // Listen for foreground messages
    this.setupForegroundListener();

    return this.token;
  }

  async registerServiceWorker() {
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );
    console.log("Service Worker registered:", registration);
    return registration;
  }

  async requestPermission() {
    const permission = await Notification.requestPermission();
    console.log("Notification permission:", permission);
    return permission;
  }

  async getFCMToken(user_id, prefix, feedback = true) {
    try {
      console.log("Requesting FCM token...");
      const token = await getToken(messaging, {
        vapidKey: vapidKey,
      });
      console.log("FCM token received:", token);

      let user = await getDocDataById("user", user_id);
      let fcm_tokens = user?.fcm_tokens || [];
      console.log("Existing tokens:", fcm_tokens);

      if (token) {
        if (!fcm_tokens.map((item) => item.value).includes(token)) {
          // Send this token to your server to store for the user
          console.log("Sending token to server...");
          await this.sendTokenToServer(prefix, token, user_id);
          return token;
        } else {
          console.log("Token already registered with server.");
          // open a dialog to inform user
          if (feedback) {
            Modal.info({
              title: "Notifications déjà activées",
              content:
                "Vous avez déjà activé les notifications pour cet appareil.",
              okText: "OK",
              onOk() {},
            });
          }
        }
      } else {
        console.log("No registration token available");
        return token;
      }
    } catch (err) {
      console.error("An error occurred while retrieving token:", err);
      throw err;
    }
  }

  setupForegroundListener() {
    onMessage(messaging, (payload) => {
      console.log("Message received in foreground:", payload);

      // Display notification using service worker for full feature support
      this.displayServiceWorkerNotification(payload.notification, payload.data);
    });
  }

  // Use service worker for rich notifications with actions
  async displayServiceWorkerNotification(notification, data = {}) {
    if (!notification || !this.serviceWorkerRegistration) return;

    const notificationTitle = notification.title || "New Message";
    const notificationOptions = {
      body: notification.body || "You have a new message",
      icon: notification.icon || "/icon.png",
      badge: "/icon.png",
      tag: "message-notification",
      requireInteraction: true,
      actions: [
        {
          action: "open",
          title: "Open",
          icon: "/open-icon.png",
        },
        {
          action: "close",
          title: "Close",
          icon: "/close-icon.png",
        },
      ],
      data: {
        ...data,
        url: window.location.origin,
        timestamp: Date.now(),
      },
    };

    try {
      await this.serviceWorkerRegistration.showNotification(
        notificationTitle,
        notificationOptions
      );
    } catch (error) {
      console.error("Error showing service worker notification:", error);
      // Fallback to simple notification
      this.displaySimpleNotification(notification);
    }
  }

  // Fallback simple notification without actions
  displaySimpleNotification(notification) {
    if (!notification) return;

    const notificationTitle = notification.title || "New Message";
    const notificationOptions = {
      body: notification.body || "You have a new message",
      icon: notification.icon || "/icon.png",
      badge: "/icon.png",
      tag: "message-notification",
      requireInteraction: true,
      // Remove actions for simple notifications
      data: notification.data || {},
    };

    new Notification(notificationTitle, notificationOptions);
  }

  async sendTokenToServer(prefix, token, user_id) {
    try {
      await fetch(prefix + "fcm/save-fcm-token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({
          token: token,
          userId: user_id,
        }),
      });
    } catch (error) {
      console.error("Error sending token to server:", error);
    }
  }

  async getAuthToken() {
    // Return your app's authentication token
    return await getOrRefreshFirebaseToken();
  }
}

// 4. USAGE EXAMPLE
class ChatApp {
  constructor() {
    this.notificationManager = new NotificationManager();
  }

  async init(user_id, prefix, feedback = true) {
    try {
      // Initialize notifications
      const token = await this.notificationManager.initialize(
        user_id,
        prefix,
        feedback
      );
      console.log("Notification system initialized with token:", token);

      // Subscribe to relevant topics
      // await this.subscribeToTopics(["general", "announcements"]);
    } catch (error) {
      console.error("Failed to initialize notifications:", error);
    }
  }

  async sendMessage(
    recipientId,
    sender_id,
    title,
    message,
    folder_id,
    prefix,
    workspaceData
  ) {
    try {
      // Send message through your chat system
      await this.sendChatMessage(recipientId, message);

      // Send push notification
      await fetch(prefix + "fcm/send-notification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: recipientId,
          title: title,
          body: message.substring(0, 100),
          data: {
            messageId: Date.now().toString(),
            senderId: sender_id,
            type: "chat_message",
            folder_id: folder_id,
            route: workspaceData
              ? `/workspace/${workspaceData.id}/messages?folder_id=${folder_id}`
              : `/messages?folder_id=${folder_id}`,
          },
        }),
      });
    } catch (error) {
      console.error("Error sending message:", error);
    }
  }

  sendChatMessage(recipientId, message) {
    // Your existing chat message logic
    return Promise.resolve();
  }
}

export { NotificationManager, ChatApp };
